import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';

/**
 * The high-level project state lifecycle phase derived from scan context:
 *   edit     — building the initial scope
 *   review   — submitted for admin review
 *   approved — approved scope exists, work in progress (including all work
 *              now complete but closeout not yet submitted)
 *   closeout — closeout has been submitted for review or approved
 *
 * Used by AdminScanPageNew and any component that needs to know what
 * project-phase the current scan is in (e.g. checkbox behavior, toolbar
 * visibility, navigator options).
 */
export type ProjectStateLifecycle = 'edit' | 'review' | 'approved' | 'closeout';

export type ProjectVersion = 'initial' | 'changeOrder';

/**
 * The general-purpose fact set for driving what any component displays for
 * the current project: which lifecycle phase, which document/scope-revision
 * lineage, whether work has started, and whether a closeout has been approved.
 */
export type ProjectLifecycleStateType = {
  phase: ProjectStateLifecycle;
  version: ProjectVersion;
  workStarted: boolean;
  /**
   * Whether every task's work is done (workStatus === 'completed'). Distinct
   * from phase === 'closeout', which additionally requires closeout to have
   * been submitted or approved - this only reflects the work itself, so
   * consumers can tell "still working" apart from "work complete, closeout
   * not yet submitted" while phase is still 'approved' for both.
   */
  workComplete: boolean;
  closeoutApproved: boolean;
  /**
   * Whether the property project itself is still editable (project.status !==
   * 'completed'). This is the robust, project-level "closeout is genuinely,
   * permanently done" signal - unlike closeoutApproved (computed from the
   * currently-viewed scan's own scanLifecycleStatus), it stays correct even
   * when viewing an auto-created child scan (approving a closeout scan
   * server-side spawns a new 'progress' child scan that starts back at
   * scanLifecycleStatus 'dispatched', which would make closeoutApproved
   * misleadingly false for that scan even though the project is done).
   */
  projectIsEditable: boolean;
  /**
   * Whether the scan currently being viewed is itself the latest approved one.
   * A change order scan keeps version: 'changeOrder' for its entire lifetime
   * (it is always compared against its original pre-change-order baseline), so
   * this is what distinguishes "change order drafted or in review" (false) from
   * "change order now approved and settled" (true) once phase is 'approved'.
   */
  scanIsLatestApproved: boolean;
  /**
   * The number of scope revisions approved so far (1 once the initial scope is
   * approved, 2 once the first change order is approved, and so on). A draft or
   * in-review change order is not yet counted here; consumers add 1 themselves
   * to display its prospective version number.
   */
  approvedScopeCount: number;
};

/**
 * Whether a change order (if any) is settled rather than still being drafted
 * or reviewed. version stays 'changeOrder' for a change order scan's entire
 * lifetime (it is always compared against its original pre-change-order
 * baseline), so scanIsLatestApproved is what actually tells "still settling"
 * apart from "now approved and settled". Always true when there is no change
 * order in play (version === 'initial').
 *
 * Shared so any component driven by ProjectLifecycleState (ProjectStageStepper,
 * and eventually ScopeNavigator) makes this call the same way rather than each
 * re-deriving it inline.
 */
export const isChangeOrderSettled = (state: Pick<ProjectLifecycleStateType, 'version' | 'scanIsLatestApproved'>): boolean =>
  state.version !== 'changeOrder' || state.scanIsLatestApproved;

/**
 * Whether the scope is genuinely, permanently locked from further edits -
 * closeout has been submitted and either approved on this scan, or the
 * project itself is marked completed (the durable signal - see
 * projectIsEditable's own doc comment for why closeoutApproved alone isn't
 * enough). Distinct from scanIsEditable, which also goes false earlier
 * during ordinary review - this is specifically "closeout is done", not
 * merely "can't edit right now". Shared so every affordance that should
 * disappear once closeout is truly done (the Focus Panel edit tab,
 * ScopeNavigator's canAddTask) uses the same definition of "locked".
 */
export const isScopeLocked = (state: Pick<ProjectLifecycleStateType, 'phase' | 'closeoutApproved' | 'projectIsEditable'>): boolean =>
  state.phase === 'closeout' && (state.closeoutApproved || !state.projectIsEditable);

/**
 * The pure derivation, kept exported for direct testing and for callers that
 * already hold the scan facts (or want to ask about a hypothetical scan).
 * Components should reach for `useProjectStateLifecycle` below instead.
 */
export const getProjectStateLifecycle = (params: {
  effectiveScanType?: string;
  scanIsSubmittedForReview?: boolean;
  haveApprovedScan?: boolean;
  scanScopeState?: string;
  scanIsChangeOrder?: boolean;
  scanIsCloseout?: boolean;
  projectIsEditable?: boolean;
}): ProjectStateLifecycle => {
  const {
    effectiveScanType,
    scanIsSubmittedForReview,
    haveApprovedScan,
    scanScopeState,
    scanIsChangeOrder,
    scanIsCloseout,
    projectIsEditable,
  } = params;

  // Closeout phase: work is done AND closeout has actually been submitted or
  // approved - not merely because every task happens to be complete. Approving
  // a completed closeout scan sets propertyProject.status to 'completed'
  // server-side (source/Models/Scan.ts's updateScan), which flips
  // projectIsEditable to false permanently, so this stays true on every future
  // visit even though scanIsSubmittedForReview itself is transient (it goes
  // false again once actually approved).
  // TASK CHECKBOX DECISION: in this phase task checkboxes would apply
  // work order status with the buttons on the group footer
  const closeoutHasBegun = (scanIsCloseout || effectiveScanType === 'progressCompleted')
    && (scanIsSubmittedForReview || !projectIsEditable);
  if (closeoutHasBegun) return 'closeout';

  // Submitted for admin review (initial scope or change order awaiting
  // sign-off - closeout submission is handled above instead, so it reaches
  // 'closeout' directly rather than this generic review phase)
  // TASK CHECKBOX DECISION: in this phase task checkboxes would apply
  // specific approval states but "Edit Request" is still only at the
  // task footer level
  if (scanIsSubmittedForReview) return 'review';

  // Approved scope exists → work phase (work not started/started, change
  // order in progress, or all work now complete but closeout not yet
  // submitted)
  // TASK CHECKBOX DECISION: in this phase task checkboxes would apply
  // work order status with the buttons on the group footer
  if (haveApprovedScan && (scanScopeState === 'current' || scanIsChangeOrder)) return 'approved';

  // Otherwise still building the initial scope
  // TASK CHECKBOX DECISION: no task checkboxes and not group footer buttons
  return 'edit';
};

/**
 * Which phase the project being viewed is in. Reads the scan facts it needs
 * from ScanEditingContext, so any component can ask for the phase directly
 * instead of being handed it down through props.
 */
export const useProjectStateLifecycle = (): ProjectStateLifecycle => {
  const scanFacts = useScanEditingContextSlice([
    'effectiveScanType',
    'scanIsSubmittedForReview',
    'haveApprovedScan',
    'scanScopeState',
    'scanIsChangeOrder',
    'scanIsCloseout',
    'projectIsEditable',
  ]);

  return getProjectStateLifecycle(scanFacts);
};
